import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  MarkerType,
  ConnectionMode,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { toPng } from 'html-to-image';
import { useGameStore } from '../../../store/gameStore';
import { gameAPI } from '../../../services/gameAPI';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from '../../../hooks/use-toast';
import {
  Grid3X3,
  Trash2,
  Download,
  StickyNote,
  RotateCcw,
  Layout,
  Save,
  Link2,
  X,
} from 'lucide-react';

// Import custom node types
import { SuspectNode } from './SuspectNode';
import { ClueNode } from './ClueNode';
import { LocationNode } from './LocationNode';
import { StickyNoteNode } from './StickyNoteNode';
import { DetailModal } from './DetailModal';
import { TutorialBanner } from '../../ui/TutorialBanner';
import { hasBeenInterviewed } from '../../../utils/actionHelpers';

// Define node types
const nodeTypes = {
  suspect: SuspectNode,
  clue: ClueNode,
  location: LocationNode,
  stickyNote: StickyNoteNode,
};

// Custom edge style for "red string" connections
const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: false,
  style: {
    stroke: '#DC143C',
    strokeWidth: 2,
  },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#DC143C',
  },
};

export const EvidenceBoard = ({ open, onClose, onSave, tutorialInfo }) => {
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const isDraggingRef = useRef(false);

  const {
    suspects,
    clues,
    locations,
    discovered,
    selectedTheme,
    caseData,
    sessionId,
    messageHistory,
    interrogationDialogues,
  } = useGameStore();

  // Theme-specific styling - defined early for use in callbacks
  const themeStyles = {
    noir: {
      accent: '#DC143C',
      background: 'bg-amber-950/90',
      boardStyle: 'Cork Board',
      edgeColor: '#DC143C',
    },
    '90s_nyc': {
      accent: '#FF6B35',
      background: 'bg-slate-800/90',
      boardStyle: 'Whiteboard',
      edgeColor: '#FF6B35',
    },
    miami_vice: {
      accent: '#FF1493',
      background: 'bg-slate-900/90',
      boardStyle: 'Glass Board',
      edgeColor: '#FF1493',
    },
  };

  const currentTheme = themeStyles[selectedTheme] || themeStyles.noir;

  // Initialize nodes from discovered items
  const initialNodes = useMemo(() => {
    const nodes = [];
    let xOffset = 0;
    let yOffset = 0;
    const spacing = 220;  // Reduced from 250 for tighter layout
    const itemsPerRow = 4;

    // Add victim node at top center
    if (caseData?.victim) {
      nodes.push({
        id: 'victim',
        type: 'suspect',
        position: { x: 400, y: 50 },
        data: {
          label: caseData.victim.name,
          image: caseData.victim.portrait,
          occupation: caseData.victim.occupation,
          background: caseData.victim.background,
          isVictim: true,
        },
      });
      yOffset = 200;
    }

    // Add discovered suspects (with defensive checks)
    const discoveredSuspects = (suspects || []).filter(s => discovered?.suspects?.includes(s.id));
    discoveredSuspects.forEach((suspect, idx) => {
      const interviewed = hasBeenInterviewed(suspect.id, interrogationDialogues, messageHistory);
      nodes.push({
        id: `suspect-${suspect.id}`,
        type: 'suspect',
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + yOffset,
        },
        data: {
          label: suspect.name,
          image: suspect.portrait,
          occupation: suspect.occupation,
          alibi: interviewed ? suspect.alibi : null, // Only show alibi if interviewed
          background: suspect.background,
          relationship: suspect.relationship,
          suspectId: suspect.id,
          interviewed, // Pass interview status for UI styling
        },
      });
    });

    // Add discovered clues (with defensive checks)
    const discoveredClues = (clues || []).filter(c => discovered?.clues?.includes(c.id));
    const clueStartY = yOffset + Math.ceil(discoveredSuspects.length / itemsPerRow) * spacing + 100;
    discoveredClues.forEach((clue, idx) => {
      nodes.push({
        id: `clue-${clue.id}`,
        type: 'clue',
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + clueStartY,
        },
        data: {
          label: clue.name,
          description: clue.description,
          type: clue.type,
          location: (locations || []).find(l => l.id === clue.location)?.name || null,
          significance: clue.significance,
          clueId: clue.id,
        },
      });
    });

    // Add discovered locations (with defensive checks)
    const discoveredLocations = (locations || []).filter(l => discovered?.locations?.includes(l.id));
    const locationStartY = clueStartY + Math.ceil(discoveredClues.length / itemsPerRow) * spacing + 100;
    discoveredLocations.forEach((location, idx) => {
      nodes.push({
        id: `location-${location.id}`,
        type: 'location',
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + locationStartY,
        },
        data: {
          label: location.name,
          description: location.description,
          locationId: location.id,
        },
      });
    });

    return nodes;
  }, [suspects, clues, locations, discovered, caseData, interrogationDialogues, messageHistory]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [noteCounter, setNoteCounter] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [detailModal, setDetailModal] = useState({ open: false, data: null });
  const [hasLoadedSavedState, setHasLoadedSavedState] = useState(false);
  const savedPositionsRef = useRef({}); // Track saved positions by node ID
  // True when THIS open's `getBoardState` rejected, so the board on screen is
  // a blank fallback rather than the player's record. Both writers are shut
  // off for that cycle — the close auto-save and the explicit Save button —
  // because a transient fetch failure must not overwrite a good server board
  // with the fallback. The ref is what the save paths read (it is current
  // inside a callback); `loadFailed` mirrors it for the parts that have to
  // re-render, since a ref does not.
  const loadFailedRef = useRef(false);
  const [loadFailed, setLoadFailed] = useState(false);

  // Tap-to-connect mode for mobile
  const [connectMode, setConnectMode] = useState(false);
  const [connectSource, setConnectSource] = useState(null); // {nodeId, nodeName}

  // Handler for viewing details
  const handleViewDetails = useCallback((data) => {
    setDetailModal({ open: true, data });
  }, []);

  // Inject onViewDetails callback and isDraggingRef into nodes
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.type === 'suspect' || node.type === 'clue' || node.type === 'location') {
          return {
            ...node,
            data: {
              ...node.data,
              onViewDetails: handleViewDetails,
              isDraggingRef,
            },
          };
        }
        return node;
      })
    );
  }, [handleViewDetails, setNodes]);

  // Track dragging to prevent click on drag
  const onNodeDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const onNodeDragStop = useCallback(() => {
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 100);
  }, []);

  // Handle node click for tap-to-connect mode
  const onNodeClick = useCallback((event, node) => {
    if (!connectMode || isDraggingRef.current) return;

    // Don't connect sticky notes
    if (node.type === 'stickyNote') {
      toast({
        title: 'Cannot connect',
        description: 'Sticky notes cannot be connected.',
      });
      return;
    }

    if (!connectSource) {
      // First tap - set as source
      setConnectSource({
        nodeId: node.id,
        nodeName: node.data.label || node.id,
      });
    } else {
      // Second tap - create connection
      if (connectSource.nodeId === node.id) {
        // Tapped same node, cancel
        setConnectSource(null);
        return;
      }

      // Check if connection already exists
      const existingEdge = edges.find(
        e => (e.source === connectSource.nodeId && e.target === node.id) ||
             (e.source === node.id && e.target === connectSource.nodeId)
      );

      if (existingEdge) {
        toast({
          title: 'Already connected',
          description: 'These items are already connected.',
        });
        setConnectSource(null);
        return;
      }

      // Create the edge
      const newEdge = {
        id: `e-${connectSource.nodeId}-${node.id}`,
        source: connectSource.nodeId,
        target: node.id,
        style: { stroke: currentTheme.edgeColor, strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: currentTheme.edgeColor,
        },
      };

      setEdges((eds) => [...eds, newEdge]);
      setConnectSource(null);

      toast({
        title: 'Connected!',
        description: `Linked ${connectSource.nodeName} to ${node.data.label || node.id}`,
      });
    }
  }, [connectMode, connectSource, edges, setEdges, currentTheme.edgeColor]);

  // Cancel connection on escape or background click
  const onPaneClick = useCallback(() => {
    if (connectMode && connectSource) {
      setConnectSource(null);
    }
  }, [connectMode, connectSource]);

  // Toggle connect mode
  const toggleConnectMode = useCallback(() => {
    setConnectMode((prev) => !prev);
    setConnectSource(null);
  }, []);

  // Sync nodes with discovered items - add new items while preserving existing positions
  useEffect(() => {
    if (!open) return;
    // Wait for saved state to be loaded first
    if (!hasLoadedSavedState) return;

    setNodes((currentNodes) => {
      // Get IDs of current evidence nodes (not sticky notes)
      const currentEvidenceIds = new Set(
        currentNodes.filter(n => n.type !== 'stickyNote').map(n => n.id)
      );

      // Get IDs from initialNodes (based on current discovered state)
      const newEvidenceIds = new Set(initialNodes.map(n => n.id));
      const freshById = new Map(initialNodes.map(n => [n.id, n]));

      // Find nodes to add (in initialNodes but not in current)
      // Use saved positions if available, otherwise use initialNodes positions
      const nodesToAdd = initialNodes
        .filter(n => !currentEvidenceIds.has(n.id))
        .map(n => {
          const savedPos = savedPositionsRef.current[n.id];
          return savedPos ? { ...n, position: savedPos } : n;
        });

      // Find sticky notes to preserve
      const stickyNotes = currentNodes.filter(n => n.type === 'stickyNote');

      // Find evidence nodes that still exist (preserve their current positions)
      let refreshed = 0;
      const existingEvidence = currentNodes
        .filter(n => n.type !== 'stickyNote' && newEvidenceIds.has(n.id))
        .map(n => {
          if (n.type !== 'clue') return n;
          const location = freshById.get(n.id)?.data?.location ?? null;
          if ((n.data?.location ?? null) === location) return n;
          refreshed += 1;
          return { ...n, data: { ...n.data, location } };
        });

      // SF-19: evidence nodes whose backing item is no longer discovered.
      // During a normal session the discovered roster only ever grows, so this
      // is always 0 and nothing is removed while playing. It is non-zero only
      // when the roster SHRINKS, which happens on a featured restart: the
      // session id (`featured:{slug}`) is unchanged, so this component keeps
      // its `nodes` state while the server reseeds `discovered`. Before this
      // branch existed the effect early-returned `currentNodes` (no additions
      // to make), the dead nodes stayed on the board, and the close auto-save
      // below wrote them back to the freshly reseeded session.
      //
      // The prune is silent for ANY id `initialNodes` no longer produces.
      // Today that set is exactly "un-discovered items", which is the intent —
      // but if the node-id scheme ever changes, every saved node written under
      // the old scheme becomes unrecognised here and is dropped (and then
      // auto-saved over on close) rather than preserved. A scheme change needs
      // a migration, not just a new builder.
      const removedCount = currentNodes.length - existingEvidence.length - stickyNotes.length;

      if (nodesToAdd.length > 0 || removedCount > 0 || refreshed > 0) {
        return [...existingEvidence, ...nodesToAdd, ...stickyNotes];
      }

      // No changes needed
      return currentNodes;
    });
  }, [open, initialNodes, setNodes, hasLoadedSavedState]);

  // SF-19: keep edges consistent with the nodes actually on the board. An edge
  // whose endpoint node is gone is dead weight that ReactFlow will not draw but
  // `saveBoard`/the close auto-save would still persist. Returning the SAME
  // array reference when nothing is dangling keeps this from re-triggering
  // itself through the `edges` dependency.
  //
  // `edges` is a dependency so the check also runs on edges created AFTER the
  // node set shrank — `onNodeClick`/`onConnect` can add one at any time and
  // would otherwise never be re-examined. `nodes` (not `initialNodes`) is the
  // honest source: the load branch below restores saved nodes that are not
  // `initialNodes`, and sticky-note deletes only touch `nodes` too. It does
  // mean the effect re-runs on every drag frame, which at this board size is a
  // Set build and one filter pass.
  useEffect(() => {
    if (!open) return;
    if (!hasLoadedSavedState) return;

    setEdges((currentEdges) => {
      const nodeIds = new Set(nodes.map((n) => n.id));
      const kept = currentEdges.filter(
        (edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target)
      );
      return kept.length === currentEdges.length ? currentEdges : kept;
    });
  }, [open, hasLoadedSavedState, nodes, edges, setEdges]);

  // `connectSource` is a snapshot of a node id taken on the FIRST tap. If that
  // node leaves the board before the second tap (the restart reseed above),
  // the second tap builds an edge with a dead endpoint, toasts "Connected!"
  // and the prune effect deletes it again. Drop the stale source instead.
  // Setting it to `null` makes the guard below short-circuit, so this cannot
  // loop.
  useEffect(() => {
    if (!connectSource) return;
    if (nodes.some((node) => node.id === connectSource.nodeId)) return;
    setConnectSource(null);
  }, [connectSource, nodes]);

  // Load saved board state when opened
  useEffect(() => {
    const loadBoardState = async () => {
      if (!open || !sessionId) return;

      // Reset state when opening
      setHasLoadedSavedState(false);
      loadFailedRef.current = false;
      setLoadFailed(false);

      try {
        setIsLoading(true);
        const savedState = await gameAPI.getBoardState(sessionId);

        if (savedState.nodes && savedState.nodes.length > 0) {
          // Store all saved positions for reference
          savedPositionsRef.current = {};
          savedState.nodes.forEach(node => {
            savedPositionsRef.current[node.id] = node.position;
          });

          // Restore saved nodes with proper callbacks for sticky notes
          const restoredNodes = savedState.nodes.map(node => {
            if (node.type === 'stickyNote') {
              return {
                ...node,
                data: {
                  ...node.data,
                  onContentChange: (id, content) => {
                    setNodes((nds) =>
                      nds.map((n) =>
                        n.id === id ? { ...n, data: { ...n.data, content } } : n
                      )
                    );
                  },
                  onColorChange: (id, color) => {
                    setNodes((nds) =>
                      nds.map((n) =>
                        n.id === id ? { ...n, data: { ...n.data, color } } : n
                      )
                    );
                  },
                  onDelete: (id) => {
                    setNodes((nds) => nds.filter((n) => n.id !== id));
                  },
                },
              };
            }
            return node;
          });

          setNodes(restoredNodes);
          setEdges(savedState.edges || []);

          // Update note counter based on saved notes
          const maxNoteNum = restoredNodes
            .filter(n => n.id.startsWith('sticky-'))
            .reduce((max, n) => {
              const num = parseInt(n.id.split('-')[1] || '0');
              return num > max ? num : max;
            }, 0);
          setNoteCounter(maxNoteNum + 1);
        } else {
          // No saved state - initialize with current discovered items.
          // SF-19: a featured restart reseeds `board_state` to
          // `{nodes: [], edges: []}` under the SAME session id, so this branch
          // IS the restart path and the component is still mounted with the
          // previous run's `edges` in state. Clearing the nodes was never
          // enough: an edge between two nodes that both survive the restart
          // (victim <-> the opening location) is not dangling, so the prune
          // effect above keeps it and the close auto-save writes the old red
          // string back onto a board the player has not drawn on yet.
          savedPositionsRef.current = {};
          setNodes(initialNodes);
          setEdges([]);
        }
      } catch (error) {
        // The request FAILED — this is not "no saved board". A network blip, a
        // 500 or an auth hiccup lands here too, so nothing the server holds may
        // be treated as gone: the edges stay in state and the close auto-save
        // below is skipped for this cycle. Only the deliberate empty-state
        // branch above clears the red string.
        loadFailedRef.current = true;
        setLoadFailed(true);
        // Initialize with current discovered items
        savedPositionsRef.current = {};
        setNodes(initialNodes);
      } finally {
        setIsLoading(false);
        setHasLoadedSavedState(true);
      }
    };

    loadBoardState();
  }, [open, sessionId]);

  // Save board state to backend
  const saveBoard = useCallback(async () => {
    // Same rule as the close auto-save, which has always waited for
    // `hasLoadedSavedState`: until the load SETTLES the board on screen is the
    // bare `initialNodes` with no edges, so writing it would erase whatever
    // the server holds. "Settled" includes the no-saved-board branch — the
    // `finally` sets the flag either way — so a first-ever open still saves.
    // `loadFailedRef` covers the third case: settled, but settled on a
    // failure. The button is disabled in both states; this guard binds.
    if (!sessionId || loadFailedRef.current || !hasLoadedSavedState) return;

    try {
      setIsSaving(true);
      const boardState = {
        nodes: nodes.map(({ id, type, position, data }) => ({
          id,
          type,
          position,
          data: {
            ...data,
            // Remove functions from data before saving
            onContentChange: undefined,
            onColorChange: undefined,
            onDelete: undefined,
          },
        })),
        edges,
      };

      await gameAPI.saveBoardState(sessionId, boardState);
      toast({
        title: 'Board saved',
        description: 'Your evidence board has been saved.',
      });
    } catch (error) {
      console.error('Failed to save board state:', error);
      toast({
        title: 'Save failed',
        description: 'Could not save board state.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [sessionId, nodes, edges, hasLoadedSavedState]);

  // Auto-save when board closes (without toast notification)
  const prevOpenRef = useRef(open);
  useEffect(() => {
    // Detect when modal transitions from open to closed
    if (prevOpenRef.current && !open && sessionId && hasLoadedSavedState
      && !loadFailedRef.current && nodes.length > 0) {
      // Save silently without showing toast
      const boardState = {
        nodes: nodes.map(({ id, type, position, data }) => ({
          id,
          type,
          position,
          data: {
            ...data,
            onContentChange: undefined,
            onColorChange: undefined,
            onDelete: undefined,
            onViewDetails: undefined,
            isDraggingRef: undefined,
          },
        })),
        edges,
      };
      gameAPI.saveBoardState(sessionId, boardState).catch(err => {
        console.error('Auto-save board failed:', err);
      });
    }
    prevOpenRef.current = open;
  }, [open, sessionId, nodes, edges, hasLoadedSavedState]);

  // Handle new connections
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            style: { stroke: currentTheme.edgeColor, strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: currentTheme.edgeColor,
            },
          },
          eds
        )
      );
    },
    [setEdges, currentTheme.edgeColor]
  );

  // Add sticky note
  const addStickyNote = useCallback(() => {
    const newNode = {
      id: `sticky-${noteCounter}`,
      type: 'stickyNote',
      position: {
        x: Math.random() * 400 + 100,
        y: Math.random() * 300 + 100,
      },
      data: {
        label: 'New Note',
        content: 'Click to edit...',
        color: 'yellow',
        onContentChange: (id, content) => {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, content } } : node
            )
          );
        },
        onColorChange: (id, color) => {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === id ? { ...node, data: { ...node.data, color } } : node
            )
          );
        },
        onDelete: (id) => {
          setNodes((nds) => nds.filter((node) => node.id !== id));
        },
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setNoteCounter((c) => c + 1);
  }, [noteCounter, setNodes]);

  // Auto-arrange nodes by type
  const autoArrange = useCallback(() => {
    const spacing = 220;  // Match initial layout spacing
    const itemsPerRow = 4;

    setNodes((nds) => {
      const suspectNodes = nds.filter(n => n.type === 'suspect');
      const clueNodes = nds.filter(n => n.type === 'clue');
      const locationNodes = nds.filter(n => n.type === 'location');
      const stickyNodes = nds.filter(n => n.type === 'stickyNote');

      let yOffset = 50;

      // Arrange suspects
      const arrangedSuspects = suspectNodes.map((node, idx) => ({
        ...node,
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + yOffset,
        },
      }));

      yOffset += Math.ceil(suspectNodes.length / itemsPerRow) * spacing + 50;

      // Arrange clues
      const arrangedClues = clueNodes.map((node, idx) => ({
        ...node,
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + yOffset,
        },
      }));

      yOffset += Math.ceil(clueNodes.length / itemsPerRow) * spacing + 50;

      // Arrange locations
      const arrangedLocations = locationNodes.map((node, idx) => ({
        ...node,
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + yOffset,
        },
      }));

      yOffset += Math.ceil(locationNodes.length / itemsPerRow) * spacing + 50;

      // Arrange sticky notes
      const arrangedStickies = stickyNodes.map((node, idx) => ({
        ...node,
        position: {
          x: (idx % itemsPerRow) * spacing + 50,
          y: Math.floor(idx / itemsPerRow) * spacing + yOffset,
        },
      }));

      return [...arrangedSuspects, ...arrangedClues, ...arrangedLocations, ...arrangedStickies];
    });
  }, [setNodes]);

  // Clear all connections
  const clearConnections = useCallback(() => {
    setEdges([]);
  }, [setEdges]);

  // Reset board to initial layout
  const resetBoard = useCallback(() => {
    setNodes(initialNodes);
    setEdges([]);
  }, [initialNodes, setNodes, setEdges]);

  // Export board as image
  const exportBoard = useCallback(() => {
    if (reactFlowWrapper.current === null) return;

    toPng(reactFlowWrapper.current, {
      backgroundColor: '#1a1a1a',
      width: reactFlowWrapper.current.offsetWidth,
      height: reactFlowWrapper.current.offsetHeight,
    })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `evidence-board-${sessionId || 'export'}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Failed to export board:', err);
      });
  }, [sessionId]);

  // Save board state
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave({
        nodes: nodes.map(({ id, type, position, data }) => ({
          id,
          type,
          position,
          data: {
            ...data,
            // Remove functions from data before saving
            onContentChange: undefined,
            onColorChange: undefined,
            onDelete: undefined,
          },
        })),
        edges,
      });
    }
  }, [nodes, edges, onSave]);

  // Fit view when opened
  useEffect(() => {
    if (open && reactFlowInstance) {
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.2 });
      }, 100);
    }
  }, [open, reactFlowInstance]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full h-[100dvh] sm:max-w-[95vw] sm:w-[95vw] sm:h-[90vh] p-0 bg-slate-900 border-2" style={{ borderColor: currentTheme.accent }}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <DialogHeader className="px-3 sm:px-4 py-2 sm:py-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center justify-between pr-10">
              <DialogTitle className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <Grid3X3 className="w-5 h-5" style={{ color: currentTheme.accent }} />
                <span className="hidden sm:inline">Evidence Board</span>
                <span className="sm:hidden">Board</span>
                <span className="text-xs sm:text-sm font-normal text-gray-400 hidden sm:inline">({currentTheme.boardStyle})</span>
              </DialogTitle>
            </div>
          </DialogHeader>

          {/* Tutorial info banner */}
          {tutorialInfo && (
            <div className="px-4 py-2 flex-shrink-0">
              <TutorialBanner title={tutorialInfo.title} content={tutorialInfo.content} />
            </div>
          )}

          {/* Board */}
          <div className="flex-1 relative" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onNodeDragStart={onNodeDragStart}
              onNodeDragStop={onNodeDragStop}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              defaultEdgeOptions={{
                ...defaultEdgeOptions,
                style: { stroke: currentTheme.edgeColor, strokeWidth: 2 },
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  color: currentTheme.edgeColor,
                },
              }}
              connectionMode={ConnectionMode.Loose}
              fitView
              snapToGrid
              snapGrid={[20, 20]}
              className={currentTheme.background}
              // Touch support for mobile
              panOnDrag={!connectMode}
              panOnScroll={false}
              zoomOnScroll
              zoomOnPinch
              zoomOnDoubleClick
              preventScrolling
            >
              <Background
                variant="dots"
                gap={20}
                size={1}
                color={`${currentTheme.accent}40`}
              />
              <Controls
                showZoom={true}
                showFitView={true}
                showInteractive={false}
              />
              <MiniMap
                nodeColor={(node) => {
                  switch (node.type) {
                    case 'suspect':
                      return node.data.isVictim ? '#ef4444' : currentTheme.accent;
                    case 'clue':
                      return '#eab308';
                    case 'location':
                      return '#3b82f6';
                    case 'stickyNote':
                      return '#fbbf24';
                    default:
                      return '#6b7280';
                  }
                }}
                maskColor="rgba(0, 0, 0, 0.8)"
                style={{ backgroundColor: '#1e293b' }}
              />

              {/* Toolbar Panel - Responsive */}
              <Panel position="top-right" className="flex gap-1 sm:gap-2 flex-wrap justify-end max-w-[50vw] sm:max-w-none">
                {/* Connect Mode Button - prominent on mobile */}
                <Button
                  onClick={toggleConnectMode}
                  size="sm"
                  variant={connectMode ? "default" : "outline"}
                  className={`min-h-touch min-w-touch sm:min-w-0 p-2 sm:px-3 ${
                    connectMode
                      ? 'text-white'
                      : 'bg-black/60 border-white/20 text-white hover:bg-white/10'
                  }`}
                  style={connectMode ? { backgroundColor: currentTheme.accent } : {}}
                  title={connectMode ? "Exit Connect Mode" : "Connect Items"}
                >
                  {connectMode ? <X className="w-4 h-4 sm:mr-1" /> : <Link2 className="w-4 h-4 sm:mr-1" />}
                  <span className="hidden sm:inline">{connectMode ? 'Cancel' : 'Connect'}</span>
                </Button>
                <Button
                  onClick={addStickyNote}
                  size="sm"
                  variant="outline"
                  className="min-h-touch min-w-touch sm:min-w-0 bg-black/60 border-white/20 text-white hover:bg-white/10 p-2 sm:px-3"
                  title="Add Note"
                >
                  <StickyNote className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Add Note</span>
                </Button>
                <Button
                  onClick={autoArrange}
                  size="sm"
                  variant="outline"
                  className="min-h-touch min-w-touch sm:min-w-0 bg-black/60 border-white/20 text-white hover:bg-white/10 p-2 sm:px-3"
                  title="Arrange"
                >
                  <Layout className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Arrange</span>
                </Button>
                <Button
                  onClick={clearConnections}
                  size="sm"
                  variant="outline"
                  className="min-h-touch min-w-touch sm:min-w-0 bg-black/60 border-white/20 text-white hover:bg-white/10 p-2 sm:px-3 hidden sm:flex"
                  title="Clear Strings"
                >
                  <Trash2 className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Clear Strings</span>
                </Button>
                <Button
                  onClick={resetBoard}
                  size="sm"
                  variant="outline"
                  className="min-h-touch min-w-touch sm:min-w-0 bg-black/60 border-white/20 text-white hover:bg-white/10 p-2 sm:px-3"
                  title="Reset"
                >
                  <RotateCcw className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Reset</span>
                </Button>
                <Button
                  onClick={exportBoard}
                  size="sm"
                  variant="outline"
                  className="min-h-touch min-w-touch sm:min-w-0 bg-black/60 border-white/20 text-white hover:bg-white/10 p-2 sm:px-3 hidden sm:flex"
                  title="Export"
                >
                  <Download className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Export</span>
                </Button>
                <Button
                  onClick={saveBoard}
                  disabled={isSaving || loadFailed || (Boolean(sessionId) && !hasLoadedSavedState)}
                  size="sm"
                  variant="outline"
                  className="min-h-touch min-w-touch sm:min-w-0 bg-black/60 border-white/20 text-white hover:bg-white/10 p-2 sm:px-3"
                  style={{ borderColor: `${currentTheme.accent}60` }}
                  title={loadFailed ? 'Cannot save: board could not be loaded' : 'Save'}
                >
                  <Save className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
                </Button>
              </Panel>

              {/* Legend Panel - Hidden on mobile, positioned to avoid overlapping with Controls */}
              <Panel position="bottom-left" className="hidden sm:block bg-black/60 p-3 rounded-lg border border-white/10 ml-14">
                <div className="text-xs text-white space-y-1">
                  <div className="font-semibold mb-2">Legend</div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span>Victim</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: currentTheme.accent }}></div>
                    <span>Suspects</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span>Clues</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span>Locations</span>
                  </div>
                </div>
              </Panel>
            </ReactFlow>
          </div>

          {/* Footer with tips - Responsive */}
          <div className="px-3 sm:px-4 py-2 border-t border-white/10 bg-black/40 flex-shrink-0">
            {/* The failed-load notice outranks the other two: it tells the
                player why Save is off. The icon and the wording carry the
                meaning, so it does not depend on colour alone. */}
            {loadFailed ? (
              <div
                role="status"
                data-testid="board-load-failed"
                className="flex items-center gap-2 text-xs text-amber-300"
              >
                <X className="w-4 h-4" />
                <span>
                  <strong>Board not loaded:</strong>{' '}
                  Your saved board could not be fetched, so it cannot be saved until you close and reopen the board.
                </span>
              </div>
            ) : connectMode ? (
              <div className="flex items-center gap-2 text-xs" style={{ color: currentTheme.accent }}>
                <Link2 className="w-4 h-4 animate-pulse" />
                <strong>Connect Mode:</strong>{' '}
                {connectSource
                  ? <span>Tap another item to connect to "{connectSource.nodeName}" (or tap background to cancel)</span>
                  : <span>Tap an item to start connecting</span>
                }
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                <strong>Tips:</strong>{' '}
                <span className="hidden sm:inline">Drag nodes to organize. Click and drag from node edges to connect elements. Double-click sticky notes to edit.</span>
                <span className="sm:hidden">Tap Connect button, then tap two items to link them. Pinch to zoom.</span>
              </p>
            )}
          </div>
        </div>

        {/* Detail Modal */}
        {detailModal.open && (
          <DetailModal
            data={detailModal.data}
            currentTheme={currentTheme}
            onClose={() => setDetailModal({ open: false, data: null })}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EvidenceBoard;
